import * as React from 'react';
import {useCallback, useImperativeHandle, useMemo, useRef, useState, useEffect} from 'react';
import {
  Dimensions,
  Keyboard,
  Platform,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import Animated, {useSharedValue, useAnimatedStyle, withTiming, Easing} from 'react-native-reanimated';
import {images} from '../../../../../../assets/icons';
import {useStyles} from '../../../../../hooks/styles';
import {useTheme} from '../../../../../theme';
import {Text} from '../../../../text';
import {BaseFieldText} from '../../text';
import {BaseFieldSelectAndroidItemProps, BaseFieldSelectProps, SelectPropsAndroid} from '../model';
import {Loader} from '../../../../loader';
import {useIsSmallScreenDevice} from '../../../../../hooks/useIsSmallScreenDevice';

const isWeb = Platform.OS === 'web';

export const BaseFieldSelectDinamic: React.FC<BaseFieldSelectProps> = (props) => {
  const {input, value, values, isSearchEnabled, setAddress, areOptionsLoading} = props;
  const pickerRef = useRef(null);
  const inputContainerRef = useRef<View>(null);
  const {input: inputStyles} = useTheme();

  const handleChange = useCallback(
    (newValue: string | number) => {
      if (value !== newValue && setAddress) {
        setAddress(() => {
          const newVal = newValue.toString();
          return newVal;
        });
      }
    },
    [value, setAddress],
  );

  const handleToggle = useCallback(() => {
    // @ts-ignore
    pickerRef.current && pickerRef.current.togglePicker();
  }, [pickerRef]);

  const [isChevronOpen, setIsChevronOpen] = useState(false);
  const blurCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleFocus = useCallback(() => {
    if (blurCloseTimer.current) {
      clearTimeout(blurCloseTimer.current);
      blurCloseTimer.current = null;
    }
    setIsChevronOpen(true);
    if (!isWeb) {
      handleToggle();
    }
  }, [handleToggle]);

  const handleBlur = useCallback(() => {
    if (isWeb) {
      setIsChevronOpen(false);
      return;
    }

    blurCloseTimer.current = setTimeout(() => {
      setIsChevronOpen(false);
      // @ts-ignore
      pickerRef.current?.closePicker?.();
    }, 200);
  }, []);

  React.useEffect(() => {
    return () => {
      if (blurCloseTimer.current) {
        clearTimeout(blurCloseTimer.current);
      }
    };
  }, []);

  const rotation = useSharedValue(0);

  React.useEffect(() => {
    if (isWeb) {
      return;
    }
    rotation.value = withTiming(isChevronOpen ? Math.PI : 0, {
      duration: 225,
      easing: Easing.inOut(Easing.ease),
    });
  }, [isChevronOpen]);

  const iconAnimatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{rotate: `${rotation.value}rad`}],
    };
  }, [rotation]);

  const webChevronStyle = useMemo(
    () => ({
      transform: [{rotate: isChevronOpen ? '180deg' : '0deg'}],
    }),
    [isChevronOpen],
  );

  const inputProps = useMemo(
    () => ({
      ...input,
      value: values?.find((item) => item.value === input.value?.toString())?.label ?? input.value,
    }),
    [input],
  );

  return (
    <View style={serviceStyles.fullWidth}>
      <TouchableWithoutFeedback style={serviceStyles.fullWidth}>
        <View
          pointerEvents={isSearchEnabled ? 'auto' : 'box-only'}
          ref={inputContainerRef}
          style={serviceStyles.fullWidth}>
          <BaseFieldText
            {...props}
            input={{
              ...inputProps,
              onChange: (val) => {
                if (!isSearchEnabled) {
                  return;
                }

                const nextValue = isWeb ? val?.target?.value : val;
                handleChange(nextValue);

                if (isWeb && isChevronOpen) {
                  handleToggle();
                }
              },
              onFocus: handleFocus,
              onBlur: handleBlur,
            }}
            right={
              <images.ChevronUpDropdown
                width={24}
                height={24}
                color={isChevronOpen ? inputStyles.common.tintColor : inputStyles.common.baseColor}
              />
            }
            rightStyle={isWeb ? webChevronStyle : iconAnimatedStyle}
            autoFocus={false}
            baseColor={isChevronOpen ? inputStyles.common.tintColor : undefined}
          />
        </View>
      </TouchableWithoutFeedback>
      <BaseFieldSelectAndroid
        // @ts-ignore ToDo
        onChange={setAddress}
        values={values || []}
        value={(value as string) || input?.value}
        placeholder={props.iosPlaceholderItem}
        ref={pickerRef}
        inputContainerRef={inputContainerRef}
        areOptionsLoading={areOptionsLoading}
      />
    </View>
  );
};

type TogglePicker = {togglePicker: () => void; closePicker: () => void};

const _BaseFieldSelectAndroid = React.forwardRef<TogglePicker, SelectPropsAndroid>((props, ref) => {
  return <InternalBaseFieldSelectAndroid {...props} forwardRef={ref} />;
});

const BaseFieldSelectAndroid = React.memo(_BaseFieldSelectAndroid);

const InternalBaseFieldSelectAndroid: React.FC<
  Omit<SelectPropsAndroid, 'forwardRef'> & {forwardRef?: React.ForwardedRef<TogglePicker>}
> = ({values, value, onChange, forwardRef, areOptionsLoading}) => {
  const isSmallScreen = useIsSmallScreenDevice();
  const styles = useStyles(({theme}) => ({
    wrap: {
      ...theme.modal.wrap,
      ...theme.input.select.wrap,
    },
    loaderInList: {
      paddingVertical: 16,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 56,
    },
    webLoader: {
      alignSelf: 'center',
      justifyContent: 'center',
      marginTop: isSmallScreen ? 0 : 50,
    },
  }));

  const [isListOpen, setIsListOpen] = useState(false);
  const shouldAutoOpenOnValuesRef = useRef(false);

  const openPicker = useCallback(() => {
    shouldAutoOpenOnValuesRef.current = true;
    setIsListOpen(true);
  }, []);
  const closePicker = useCallback(() => {
    shouldAutoOpenOnValuesRef.current = false;
    setIsListOpen(false);
  }, []);

  const handleChange = useCallback(
    (newValue: string | number) => {
      onChange(newValue as string);
      closePicker();
      if (!isWeb) {
        Keyboard.dismiss();
      }
    },
    [onChange, closePicker],
  );

  useEffect(() => {
    if (!shouldAutoOpenOnValuesRef.current) {
      return;
    }
    if (values?.length || (!isWeb && areOptionsLoading)) {
      setIsListOpen(true);
    }
  }, [values, areOptionsLoading]);

  useImperativeHandle(
    forwardRef,
    () => ({
      togglePicker: openPicker,
      closePicker,
    }),
    [openPicker, closePicker],
  );

  const windowHeight = Dimensions.get('window').height || 0;
  const maxHeight = Math.min(windowHeight * (isSmallScreen ? 0.35 : 0.5), isSmallScreen ? 280 : 420);

  if (isWeb && isListOpen && (areOptionsLoading || (values.length === 0 && value !== ''))) {
    return (
      <View style={styles.webLoader}>
        <Loader />
      </View>
    );
  }

  const listItems = (
    <>
      {!isWeb && areOptionsLoading ? (
        <View style={styles.loaderInList} pointerEvents='none'>
          <Loader />
        </View>
      ) : null}
      {values.map((v, idx) => (
        <BaseFieldSelectAndroidItem
          id={v.id}
          key={v.value}
          label={v.value}
          value={v.value}
          onSelect={handleChange}
          selected={v.value === value}
          isLastItem={idx === values.length - 1}
          itemNumber={idx}
        />
      ))}
    </>
  );

  if (isWeb) {
    if (!isListOpen || !values.length) {
      return null;
    }

    return (
      <Animated.ScrollView
        keyboardShouldPersistTaps='handled'
        style={[
          {
            position: 'relative',
            width: '100%',
            maxHeight,
            zIndex: 10,
          },
          styles.wrap,
        ]}>
        {listItems}
      </Animated.ScrollView>
    );
  }

  if (!isListOpen) {
    return null;
  }

  return (
    <Animated.ScrollView
      keyboardShouldPersistTaps='handled'
      nestedScrollEnabled
      style={[styles.wrap, {maxHeight, width: '100%'}]}>
      {listItems}
    </Animated.ScrollView>
  );
};

const _BaseFieldSelectAndroidItem: React.FC<BaseFieldSelectAndroidItemProps> = ({
  id,
  label,
  value,
  selected,
  onSelect,
  isLastItem,
  itemNumber,
}) => {
  const styles = useStyles(({theme}) => ({
    item: {
      paddingVertical: 12,
      paddingHorizontal: 16,
      ...(isLastItem ? {} : theme.hr),
    },
    itemText: {
      ...theme.fonts.body,
      color: theme.color.textColor,
    },
    activeItem: {
      backgroundColor: theme.color.primaryColor,
    },
    activeItemText: {
      color: theme.color.whiteTextColor,
    },
  }));
  const handleSelect = () => onSelect(value);

  return (
    <TouchableOpacity
      testID={`andoird-select-item-${itemNumber || id}-id`}
      onPress={handleSelect}
      style={[styles.item, selected ? styles.activeItem : {}]}>
      <Text style={[styles.itemText, selected ? styles.activeItemText : {}]}>{label}</Text>
    </TouchableOpacity>
  );
};

const BaseFieldSelectAndroidItem = React.memo(_BaseFieldSelectAndroidItem);

const serviceStyles = StyleSheet.create({
  fullWidth: {width: '100%'},
});
